import type { BadgeId, RankId } from '@rw/shared';
import { DEFAULT_RULES, rankForLevel } from '@rw/game-engine';
import { locale, t } from '../../i18n';

/** Look of each rank tier: badge colours (fill, text) and an icon. */
export const RANK_STYLE: Record<RankId, { bg: string; fg: string; icon: string; name: string }> = {
  rookie: { bg: '#b6ff3b', fg: '#06090d', icon: '🥾', name: 'Rookie' },
  bronze: {
    bg: 'linear-gradient(135deg,#e0a26b,#9c5a2b)',
    fg: '#1a0d04',
    icon: '🥉',
    name: 'Bronze',
  },
  silver: {
    bg: 'linear-gradient(135deg,#f1f5f9,#94a3b8)',
    fg: '#0f172a',
    icon: '🥈',
    name: 'Silver',
  },
  gold: { bg: 'linear-gradient(135deg,#ffe58a,#ffb020)', fg: '#2a1a00', icon: '🥇', name: 'Gold' },
  platinum: {
    bg: 'linear-gradient(135deg,#a5f3fc,#38bdf8)',
    fg: '#06222e',
    icon: '💠',
    name: 'Platinum',
  },
  diamond: {
    bg: 'linear-gradient(135deg,#c4b5fd,#7c3aed)',
    fg: '#ffffff',
    icon: '💎',
    name: 'Diamond',
  },
  master: {
    bg: 'linear-gradient(135deg,#fca5a5,#e11d48)',
    fg: '#ffffff',
    icon: '🔥',
    name: 'Master',
  },
  legend: {
    bg: 'linear-gradient(135deg,#fde68a,#f97316,#be123c)',
    fg: '#ffffff',
    icon: '👑',
    name: 'Legend',
  },
};

export const rankOf = (level: number) => rankForLevel(level);
export const rankName = (rank: RankId) => t(RANK_STYLE[rank].name);
/** First level of a rank tier. */
export const rankMinLevel = (rank: RankId) => DEFAULT_RULES.ranks[rank];

interface BadgeInfo {
  icon: string;
  name: string;
  /** What a tier asks for, given its threshold. */
  goal: (n: number) => string;
}

const km = (m: number) =>
  m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 })} km`;
const longRun = (m: number) =>
  m === 21_097 ? t('a half marathon') : m === 42_195 ? t('a marathon') : km(m);

export const BADGE_INFO: Record<BadgeId, BadgeInfo> = {
  distance: { icon: '📏', name: 'Distance', goal: (n) => t('Run {d} in total', { d: km(n) }) },
  longest_run: {
    icon: '🛣️',
    name: 'Long run',
    goal: (n) => t('Run {d} in one run', { d: longRun(n) }),
  },
  streak: { icon: '🔥', name: 'Streak', goal: (n) => t('Run {n} days in a row', { n }) },
  runs: { icon: '👟', name: 'Regular', goal: (n) => t('Complete {n} valid runs', { n }) },
  captures: { icon: '⬢', name: 'Conqueror', goal: (n) => t('Capture {n} sectors', { n }) },
  held: { icon: '🏰', name: 'Landlord', goal: (n) => t('Hold {n} sectors at once', { n }) },
  steals: {
    icon: '⚔️',
    name: 'Raider',
    goal: (n) => t('Take {n} sectors from other runners', { n }),
  },
  revenge: {
    icon: '🗡️',
    name: 'Revenge',
    goal: (n) => t('Take back {n} sectors within 24 h of losing them', { n }),
  },
  explorer: {
    icon: '🧭',
    name: 'Explorer',
    goal: (n) => t('Capture {n} different sectors', { n }),
  },
  early_bird: {
    icon: '🌅',
    name: 'Early bird',
    goal: (n) => t('{n} runs of 1 km+ starting before 5 am', { n }),
  },
  night_owl: {
    icon: '🌙',
    name: 'Night owl',
    goal: (n) => t('{n} runs of 1 km+ starting after 9 pm', { n }),
  },
  events_completed: {
    icon: '🏁',
    name: 'Finisher',
    goal: (n) => t('Qualify in {n} events', { n }),
  },
  event_podium: { icon: '🏆', name: 'Podium', goal: () => t('Finish in the top 3 of an event') },
  team_champion: {
    icon: '🛡️',
    name: 'Team champion',
    goal: () => t('Win an event with your team'),
  },
  organizer: { icon: '📣', name: 'Organiser', goal: () => t('Organise an event') },
  club_member: { icon: '🤝', name: 'Club member', goal: () => t('Join a club') },
};

export const TIER_NAMES = ['Bronze', 'Silver', 'Gold'];
/** Frame colours per tier; single-tier badges use the special purple. */
export const TIER_COLORS = [
  ['#e0a26b', '#8a4b1f'],
  ['#f1f5f9', '#7b8a9e'],
  ['#ffe58a', '#e09400'],
];
export const SPECIAL_COLORS = ['#d8b4fe', '#7c3aed'];

export const badgeName = (id: BadgeId) => t(BADGE_INFO[id].name);
export const isMultiTier = (id: BadgeId) => DEFAULT_RULES.badges.tiers[id].length > 1;
/** "Distance · Silver", or just the name for single-tier badges. */
export function badgeTierName(id: BadgeId, tier: number) {
  return isMultiTier(id) ? `${badgeName(id)} · ${t(TIER_NAMES[tier - 1] ?? '')}` : badgeName(id);
}

/** A badge value formatted like its threshold (km for distances, a count otherwise). */
export function badgeValue(id: BadgeId, v: number) {
  return id === 'distance' || id === 'longest_run' ? km(v) : String(Math.floor(v));
}
